import { useEffect, useState, useRef } from 'react'

import {
  Modal,
  TextInput,
  Box,
  Divider,
  ActionIcon,
  Tooltip,
  Input,
  Menu,
} from '@mantine/core'
import { Dropzone, IMAGE_MIME_TYPE, PDF_MIME_TYPE } from '@mantine/dropzone'
import { useForm } from '@mantine/form'
import { Paperclip, Trash2 } from 'lucide-react'
import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import { useMutation, useQuery } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import Tag from 'src/components/DataDisplay/Tag/Tag'
import CustomFieldVariableMenu from 'src/components/Overlays/CustomFieldVariableMenu/CustomFieldVariableMenu'
import { toast } from 'src/components/Overlays/Toast/Toast'
import BodyTextArea from 'src/components/Overlays/WriteEmailModal/components/BodyTextArea'
import StyledMenu from 'src/components/Overlays/StyledMenu/StyledMenu'
import {
  CREATE_EMAIL_TEMPLATE,
  EDIT_EMAIL_TEMPLATE,
} from 'src/graphql/emailTemplates.graphql'
import { FileContentType } from 'src/graphql/types/documents'
import { GET_CUSTOM_FIELDS } from 'src/graphql/customFields.graphql'
import { focusCaretAfterInsert, insertAtCaret } from 'src/lib/textInput.utils'
import { TextInputMentions } from 'src/lib/textInputMentions'

//This should reflect variables found in api/src/lib/textTemplate/textTemplate.ts
const TemplateVariables = [
  { name: 'Company Name', value: 'CompanyName' },
  { name: 'Employee Full Name', value: 'EmployeeFullName' },
  { name: 'Employee First Name', value: 'EmployeeFirstName' },
  { name: 'Employee Phone Number', value: 'EmployeePhoneNumber' },
  { name: 'Applicant Full Name', value: 'ApplicantFullName' },
  { name: 'Applicant First Name', value: 'ApplicantFirstName' },
  { name: 'Application Link', value: 'ApplicationLink' },
  { name: 'Review Link', value: 'ReviewLink' },
  { name: 'Job Listing Name', value: 'JobListingName' },
]

// Mapping from MIME types to FileContentType enum values
const fileContentTypeByMimeType = {
  'image/png': FileContentType.PNG,
  'image/jpeg': FileContentType.JPG,
  'image/jpg': FileContentType.JPG,
  'image/tiff': FileContentType.TIF,
  'image/gif': FileContentType.GIF,
  'image/heic': FileContentType.HEIC,
  'application/pdf': FileContentType.PDF,
  'text/html': FileContentType.HTML,
  'application/msword': FileContentType.DOC,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    FileContentType.DOC,
  'application/zip': FileContentType.ZIP,
  'text/csv': FileContentType.CSV,
  'text/plain': FileContentType.TXT,
  'audio/wav': FileContentType.WAV,
  'audio/x-wav': FileContentType.WAV,
  'video/mp4': FileContentType.MP4,
}

const CreateTemplateModal = ({
  emailTemplate,
  isOpen,
  refetchQueries,
  onClose,
  edit = false,
}) => {
  const [cursorPosition, setCursorPosition] = useState(0)
  const subjectRef = useRef<HTMLInputElement | null>(null)
  const skipOpenRef = useRef(false)
  const [mentionOpen, setMentionOpen] = useState(false)
  const [selectedWord, setSelectedWord] = useState('')
  const [activeField, setActiveField] = useState<'subject' | 'body'>('subject')
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [existingAttachments, setExistingAttachments] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isUploadingFiles, setIsUploadingFiles] = useState(false)
  const [bodyEditorRef, setBodyEditorRef] = useState(null)
  const uploadRef = useRef<() => void>(null)

  // Combined loading state
  const loading = isSubmitting || isUploadingFiles

  const MAX_ATTACHMENT_SIZE = 2.9 * 1024 * 1024 // 2.9MB

  const handleFileDrop = (files: File[]) => {
    console.log(
      '📁 handleFileDrop called with files:',
      files.map((f) => ({ name: f.name, size: f.size, type: f.type }))
    )

    const currentSize = selectedFiles.reduce((acc, file) => acc + file.size, 0)
    const newSize = files.reduce((acc, file) => acc + file.size, 0)

    console.log('📊 File size analysis:', {
      currentSize,
      newSize,
      totalSize: currentSize + newSize,
      maxSize: MAX_ATTACHMENT_SIZE,
      withinLimit: currentSize + newSize <= MAX_ATTACHMENT_SIZE,
    })

    if (currentSize + newSize > MAX_ATTACHMENT_SIZE) {
      console.warn('⚠️ Attachment size limit exceeded')
      toast('Attachment size limit reached (2.9MB)', 'warning')
      return
    }

    console.log('✅ Files accepted, adding to selectedFiles')
    setSelectedFiles([...selectedFiles, ...files])
  }

  // A picked file replaces the attachment sharing its name — repairing a broken
  // attachment is exactly that flow, and keeping both would leave every send
  // failing on the old one. Derived rather than dropped on pick, so dismissing
  // the file brings the attachment back instead of deleting a row the recruiter
  // never confirmed removing. On edit save, these ids become keptAttachmentIds
  // and selectedFiles become newAttachments.
  const replacedFileNames = new Set(selectedFiles.map((file) => file.name))
  const attachmentsToKeep = existingAttachments.filter(
    (att) => !replacedFileNames.has(att.fileName)
  )

  //FORM SETUP:
  const schema = z.object({
    title: z
      .string()
      .min(1, { message: 'Please include a title' })
      .max(100, { message: 'Template title is too long' }),
    subject: z
      .string()
      .min(1, { message: 'Please include a subject' })
      .max(200, { message: 'Template subject is too long' }),
    body: z
      .string()
      .min(1, { message: 'Please include a body' })
      // Keep 1000 characters for dynamic variables and other content
      .max(9000, { message: 'Template body is too long' }),
  })

  const form = useForm({
    initialValues: {
      title: emailTemplate?.title || '',
      subject: emailTemplate?.subject || '',
      body: emailTemplate?.body || '',
    },
    validate: zodResolver(schema),
  })

  useEffect(() => {
    form.setValues({
      title: emailTemplate?.title || '',
      subject: emailTemplate?.subject || '',
      body: emailTemplate?.body || '',
    })
    // Load existing attachments when editing, clear when creating new
    if (edit && emailTemplate?.attachments) {
      setExistingAttachments(emailTemplate.attachments)
    } else {
      setExistingAttachments([])
    }
  }, [emailTemplate, edit])

  // Clear all state when modal opens for new template creation
  useEffect(() => {
    if (isOpen && !edit) {
      setSelectedFiles([])
      setExistingAttachments([])
      form.reset()
    }
  }, [isOpen, edit])

  // Load active custom fields to offer as variables
  const { data: customFieldsData } = useQuery(GET_CUSTOM_FIELDS, {
    variables: { status: 'ACTIVE', pageInfo: { limit: 200, offset: 0 } },
  })
  const customFieldVariables =
    customFieldsData?.customFields?.items?.map((f) => ({
      name: f.label,
      value: `cf:${f.key}`,
    })) ?? []

  // @ variable picker for Subject field (shared rule)
  useEffect(() => {
    if (activeField !== 'subject') return setMentionOpen(false)
    if (skipOpenRef.current) {
      skipOpenRef.current = false
      setMentionOpen(false)
      return
    }
    const query = TextInputMentions.getTriggerQuery(
      String(form.values.subject ?? ''),
      Math.max(0, Math.min(cursorPosition, String(form.values.subject ?? '').length))
    )
    if (query !== null) {
      setSelectedWord(query)
      setMentionOpen(true)
    } else {
      setMentionOpen(false)
    }
  }, [form.values.subject, cursorPosition, activeField])

  const insertSubjectVariableAtCaret = (rawValue: string) => {
    const token = `{{${rawValue}}}`
    const { content, caret } = insertAtCaret(
      subjectRef.current,
      String(form.values.subject ?? ''),
      token
    )
    skipOpenRef.current = true
    form.setFieldValue('subject', content)
    setCursorPosition(caret)
    focusCaretAfterInsert(subjectRef.current, caret)
  }

  //MUTATIONS:
  const [editEmailTemplate] = useMutation(EDIT_EMAIL_TEMPLATE, {
    refetchQueries: refetchQueries,
    onCompleted: async (data) => {
      console.log('🎉 editEmailTemplate completed with data:', data)
      console.log('🔍 FULL RESPONSE DEBUG:', JSON.stringify(data, null, 2))
      console.log('📁 selectedFiles.length:', selectedFiles.length)
      console.log(
        '📎 data.updateEmailTemplate?.attachments:',
        data.updateEmailTemplate?.attachments
      )
      console.log(
        '🔍 data.updateEmailTemplate keys:',
        Object.keys(data.updateEmailTemplate || {})
      )
      console.log(
        '🔍 data.updateEmailTemplate full object:',
        data.updateEmailTemplate
      )
      console.log('📊 Mutation response analysis:', {
        hasUpdateEmailTemplate: !!data.updateEmailTemplate,
        hasAttachments: !!data.updateEmailTemplate?.attachments,
        attachmentsCount: data.updateEmailTemplate?.attachments?.length || 0,
        selectedFilesCount: selectedFiles.length,
        shouldUploadFiles:
          selectedFiles.length > 0 && !!data.updateEmailTemplate?.attachments,
      })

      // Handle file uploads after template update
      if (selectedFiles.length > 0 && data.updateEmailTemplate?.attachments) {
        console.log('🔄 Starting file uploads after template update...')
        await handleFileUploads(data.updateEmailTemplate.attachments)
      } else {
        console.log('ℹ️ No file uploads needed, showing success toast')
        toast('Your email template was updated successfully', 'success')
        handleClose()
      }
    },
    onError: (error) => {
      console.error('❌ editEmailTemplate error:', error)
      console.error('❌ editEmailTemplate error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError,
      })
      toast('Unable to update email template, please try again.', 'error')
      setIsSubmitting(false)
      setIsUploadingFiles(false)
    },
  })

  const [createEmailTemplate] = useMutation(CREATE_EMAIL_TEMPLATE, {
    refetchQueries: refetchQueries,
    onCompleted: async (data) => {
      console.log('🎉 createEmailTemplate completed with data:', data)
      console.log('📁 selectedFiles.length:', selectedFiles.length)
      console.log(
        '📎 data.createEmailTemplate?.attachments:',
        data.createEmailTemplate?.attachments
      )
      console.log('📊 Mutation response analysis:', {
        hasCreateEmailTemplate: !!data.createEmailTemplate,
        hasAttachments: !!data.createEmailTemplate?.attachments,
        attachmentsCount: data.createEmailTemplate?.attachments?.length || 0,
        selectedFilesCount: selectedFiles.length,
        shouldUploadFiles:
          selectedFiles.length > 0 && !!data.createEmailTemplate?.attachments,
      })

      // Handle file uploads after template creation
      if (selectedFiles.length > 0 && data.createEmailTemplate?.attachments) {
        console.log('🔄 Starting file uploads after template creation...')
        await handleFileUploads(data.createEmailTemplate.attachments)
      } else {
        console.log('ℹ️ No file uploads needed, showing success toast')
        toast('A new email template was created successfully', 'success')
        handleClose()
      }
    },
    onError: (error) => {
      console.error('❌ createEmailTemplate error:', error)
      console.error('❌ createEmailTemplate error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError,
      })
      toast('Unable to create email template, please try again.', 'error')
      setIsSubmitting(false)
      setIsUploadingFiles(false)
    },
  })

  // Function to handle S3 file uploads
  const handleFileUploads = async (attachments) => {
    console.log('🔄 handleFileUploads called with:', {
      attachments: attachments?.length || 0,
      selectedFiles: selectedFiles.length,
    })

    if (!selectedFiles.length) {
      console.log('ℹ️ No files to upload, showing success and closing')
      // No files to upload, just show success and close
      toast(
        edit
          ? 'Your email template was updated successfully'
          : 'A new email template was created successfully',
        'success'
      )
      handleClose()
      return
    }

    try {
      setIsUploadingFiles(true)
      console.log('🔍 Starting file uploads...')
      console.log(
        '📁 Selected files:',
        selectedFiles.map((f) => ({ name: f.name, size: f.size, type: f.type }))
      )
      console.log('📎 Attachments from backend:', attachments)

      if (!attachments || attachments.length === 0) {
        console.error('❌ No upload URLs received from server')
        throw new Error('No upload URLs received from server')
      }

      console.log(
        '🔗 Available upload URLs:',
        attachments.map((att) => ({
          fileName: att.fileName,
          hasUploadUrl: !!att.uploadUrl,
          uploadUrlLength: att.uploadUrl?.length,
        }))
      )

      // Only newAttachments rows carry an upload URL; keptAttachmentIds rows
      // come back without one, and a replacement can share a name with the
      // attachment it replaces. Searching the whole list by name would bind the
      // new bytes to the kept row and skip the upload entirely.
      // Two picked files can share a name, so claim a distinct row per file:
      // reusing one would send both PUTs to the same object and leave the other
      // row pointing at nothing.
      const unclaimed = attachments.filter((att) => att.uploadUrl)
      const uploadTargetByIndex = selectedFiles.map((file) => {
        const match = unclaimed.findIndex((att) => att.fileName === file.name)
        return match === -1 ? undefined : unclaimed.splice(match, 1)[0]
      })

      // Upload all files in parallel
      const uploadPromises = selectedFiles.map(async (file, index) => {
        console.log(
          `🔍 Processing file ${index + 1}/${selectedFiles.length}: "${
            file.name
          }"`
        )

        const attachment = uploadTargetByIndex[index]
        console.log(
          `🔗 Matching file "${file.name}" with attachment:`,
          attachment
            ? {
                fileName: attachment.fileName,
                hasUploadUrl: !!attachment.uploadUrl,
                uploadUrlPreview:
                  attachment.uploadUrl?.substring(0, 100) + '...',
              }
            : 'NOT FOUND'
        )

        if (!attachment || !attachment.uploadUrl) {
          console.error(`❌ No upload URL found for file "${file.name}"`)
          throw new Error(
            `No upload URL found for file "${file.name}". Please try again.`
          )
        }

        console.log(
          `⬆️ Uploading file "${file.name}" (${file.size} bytes) (${
            index + 1
          }/${selectedFiles.length}) to S3...`
        )

        const uploadStartTime = Date.now()
        const response = await fetch(attachment.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
          },
        })
        const uploadEndTime = Date.now()

        console.log(`📤 Upload response for "${file.name}":`, {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          uploadTime: `${uploadEndTime - uploadStartTime}ms`,
          responseHeaders: Object.fromEntries(response.headers.entries()),
        })

        if (!response.ok) {
          console.error(`❌ Upload failed for "${file.name}":`, {
            status: response.status,
            statusText: response.statusText,
            url: attachment.uploadUrl.substring(0, 100) + '...',
          })
          throw new Error(
            `Failed to upload ${file.name}: ${response.status} ${response.statusText}`
          )
        }

        console.log(
          `✅ Successfully uploaded "${file.name}" in ${
            uploadEndTime - uploadStartTime
          }ms`
        )
        return { fileName: file.name, success: true }
      })

      console.log('⏰ Waiting for all uploads to complete...')
      // Wait for all uploads to complete
      const results = await Promise.all(uploadPromises)
      const successCount = results.filter((r) => r.success).length

      console.log(
        `🎉 All uploads completed! ${successCount}/${selectedFiles.length} files uploaded successfully`
      )
      console.log('📊 Upload results:', results)

      toast(
        edit
          ? `Email template updated with ${successCount} attachment${
              successCount !== 1 ? 's' : ''
            }`
          : `Email template created with ${successCount} attachment${
              successCount !== 1 ? 's' : ''
            }`,
        'success'
      )
      handleClose()
    } catch (error) {
      console.error('❌ Error uploading files:', error)
      console.error('❌ Upload error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name,
      })
      toast(
        `Template ${edit ? 'updated' : 'created'} but file upload failed: ${
          error.message
        }`,
        'error'
      )
      // Don't close the modal so user can try again
      setIsUploadingFiles(false)
    }
  }

  const onFormSubmit = async (values) => {
    try {
      console.log('🚀 onFormSubmit started with values:', values)
      console.log('📊 Current state:', {
        selectedFiles: selectedFiles.length,
        existingAttachments: existingAttachments.length,
        edit,
        emailTemplateId: edit ? emailTemplate?.emailTemplateId : 'new',
      })

      setIsSubmitting(true)

      // Files just picked — on edit these go out as newAttachments (always a
      // new row + upload URL). On create they are the whole payload.
      const newAttachments = selectedFiles.map((file) => ({
        fileName: file.name,
        fileType:
          fileContentTypeByMimeType[file.type] || FileContentType.UNKNOWN,
      }))

      console.log('📁 selectedFiles:', selectedFiles)
      console.log('📎 newAttachments:', newAttachments)

      // Flattened name-only list for createEmailTemplate, which has no keep
      // vs upload split — there is nothing to keep yet.
      const existingAttachmentsToKeep = attachmentsToKeep.map((att) => ({
        fileName: att.fileName,
        fileType: att.fileType, // These are already in FileContentType format
      }))

      console.log('📎 existingAttachmentsToKeep:', existingAttachmentsToKeep)

      const allAttachments = [...existingAttachmentsToKeep, ...newAttachments]

      console.log('📎 allAttachments:', allAttachments)

      // Edit: keptAttachmentIds = existing rows still shown; newAttachments =
      // selectedFiles. Create still sends the legacy `attachments` list —
      // there is nothing to keep yet, so every file is new.
      const templateData = edit
        ? {
            ...values,
            keptAttachmentIds: attachmentsToKeep.map(
              (att) => att.emailAttachmentId
            ),
            newAttachments,
          }
        : {
            ...values,
            attachments: allAttachments.length > 0 ? allAttachments : undefined,
          }

      console.log('📋 templateData:', templateData)

      // Create/update template first
      if (edit) {
        console.log('🔄 Calling editEmailTemplate with:', {
          emailTemplateId: emailTemplate.emailTemplateId,
          input: templateData,
        })
        console.log('⏰ Starting editEmailTemplate mutation...')
        await editEmailTemplate({
          variables: {
            emailTemplateId: emailTemplate.emailTemplateId,
            input: templateData,
          },
        })
        console.log('✅ editEmailTemplate completed successfully')
      } else {
        console.log('🆕 Calling createEmailTemplate with:', {
          input: templateData,
        })
        console.log('⏰ Starting createEmailTemplate mutation...')
        await createEmailTemplate({
          variables: {
            input: templateData,
          },
        })
        console.log('✅ createEmailTemplate completed successfully')
      }

      // Note: File uploads are handled in the onCompleted callbacks
      // The button will remain disabled until uploads complete
      console.log('📤 Form submission completed, waiting for file uploads...')
    } catch (error) {
      console.error('❌ Error in form submission:', error)
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name,
      })
      toast(error.message, 'error')
      setIsSubmitting(false)
      setIsUploadingFiles(false)
    }
  }

  const insertVariable = (variable: string) => {
    if (activeField === 'subject') {
      const subjectValue = form.values.subject as string
      const { content, caret } = insertAtCaret(
        subjectRef.current,
        String(subjectValue ?? ''),
        `{{${variable}}}`
      )
      skipOpenRef.current = true
      form.setFieldValue('subject', content)
      setCursorPosition(caret)
      focusCaretAfterInsert(subjectRef.current, caret)
    } else if (activeField === 'body') {
      // For the body, insert the variable at the current cursor position in the rich text editor
      if (bodyEditorRef && bodyEditorRef.commands) {
        bodyEditorRef.commands.insertContent(`{{${variable}}}`)
      } else {
        // Fallback to appending at the end if editor ref is not available
        const newValue = `${form.values.body || ''}{{${variable}}}`
        form.setFieldValue('body', newValue)
      }
    }
  }

  const handleClose = () => {
    console.log('🔄 handleClose called')
    console.log('📊 Cleanup state:', {
      selectedFiles: selectedFiles.length,
      existingAttachments: existingAttachments.length,
      isSubmitting,
      isUploadingFiles,
    })

    form.reset()
    setSelectedFiles([])
    setExistingAttachments([])
    setIsSubmitting(false)
    setIsUploadingFiles(false)
    onClose()

    console.log('✅ Modal closed and state cleaned up')
  }

  const removeExistingAttachment = (attachmentId: string) => {
    setExistingAttachments(
      existingAttachments.filter(
        (att) => att.emailAttachmentId !== attachmentId
      )
    )
  }

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title={edit ? 'Edit Email Template' : 'Create Email Template'}
      closeOnEscape
      stackId="create-template-modal"
      closeOnClickOutside
      transitionProps={{
        transition: 'slide-up',
        duration: 300,
        timingFunction: 'ease',
      }}
      overlayProps={{
        backgroundOpacity: 0.05,
      }}
      classNames={{
        title: 'font-semibold',
        body: 'flex flex-col gap-2 overflow-y-auto h-full',
        content:
          'fixed bottom-1.5 right-1.5 m-0 transition-all duration-300 rounded-2xl shadow-lg max-h-[100vh] w-[45vw]',
      }}
    >
      <div className="flex flex-col gap-6">
        <form onSubmit={form.onSubmit(onFormSubmit)}>
          <div className="flex flex-col gap-2">
            <div className="flex flex-row items-center gap-2">
              <Input.Label>Name:</Input.Label>
              <TextInput
                variant="unstyled"
                className="flex-1"
                placeholder={
                  form.values.title
                    ? (form.values.title as string)
                    : 'Template Name'
                }
                // label="Template Name"
                {...form.getInputProps('title')}
              />
            </div>

            <Divider />
            <div className="flex flex-row items-center gap-2">
              <Input.Label>Subject:</Input.Label>
              <StyledMenu opened={mentionOpen}>
                <Menu.Target>
                  <TextInput
                    ref={subjectRef}
                    variant="unstyled"
                    className="flex-1"
                    placeholder="Subject"
                    {...form.getInputProps('subject')}
                    onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                      setActiveField('subject')
                      setCursorPosition(
                        (e.target as HTMLInputElement).selectionStart || 0
                      )
                    }}
                    onChange={(e) => {
                      form.setFieldValue('subject', e.target.value)
                      setCursorPosition(
                        (e.target as HTMLInputElement).selectionStart || 0
                      )
                    }}
                  />
                </Menu.Target>
                <Menu.Dropdown
                  className="max-h-[300px] overflow-y-auto"
                  onFocus={() => subjectRef.current?.focus()}
                >
                  {[...TemplateVariables, ...customFieldVariables]
                    .filter((v) =>
                      v.name.toLowerCase().includes(selectedWord.toLowerCase())
                    )
                    .map((v) => (
                      <Menu.Item
                        key={v.value}
                        onClick={() => {
                          const token = `{{${v.value}}}`
                          const { content, caret } = TextInputMentions.replaceAtTrigger(
                            subjectRef.current,
                            String(form.values.subject ?? ''),
                            token
                          )
                          skipOpenRef.current = true
                          form.setFieldValue('subject', content)
                          setCursorPosition(caret)
                          focusCaretAfterInsert(subjectRef.current, caret)
                          setMentionOpen(false)
                        }}
                        className="text-xs"
                      >
                        {v.name}
                      </Menu.Item>
                    ))}
                </Menu.Dropdown>
              </StyledMenu>
            </div>
            <Divider />
            <div className="flex flex-col gap-4">
              <Input.Label>
                Click on the variable to insert it in the template:
              </Input.Label>
              <div className="flex flex-row flex-wrap gap-2">
                {TemplateVariables.map((variable) => (
                  <button
                    key={variable.value}
                    type="button"
                    onClick={() => insertVariable(variable.value)}
                  >
                    <Tag showCloseButton={false} size="xs">
                      {variable.name}
                    </Tag>
                  </button>
                ))}
                <CustomFieldVariableMenu
                  variables={customFieldVariables}
                  onSelect={insertVariable}
                  renderTarget={(label) => (
                    <Tag showCloseButton={false} size="xs">
                      {label}
                    </Tag>
                  )}
                />
              </div>
            </div>
            <Divider />
            <Box onClick={() => setActiveField('body')}>
              <BodyTextArea
                form={form}
                enableVariableMentions
                variableItems={[...TemplateVariables, ...customFieldVariables]}
                onEditorReady={(editor) => {
                  setBodyEditorRef(editor)
                  editor.on('focus', () => setActiveField('body'))
                }}
              />
            </Box>
          </div>

          <Divider className="my-4" />
          <div className="flex flex-row items-center gap-4">
            <Dropzone
              openRef={uploadRef}
              onDrop={(files) => {
                handleFileDrop(files)
              }}
              accept={[...IMAGE_MIME_TYPE, ...PDF_MIME_TYPE]}
              maxFiles={10}
              className="hidden"
            />
            <Tooltip
              label="Attach files"
              position="bottom"
              withArrow
              arrowPosition="center"
            >
              <ActionIcon
                color="gray"
                variant="subtle"
                onClick={() => uploadRef.current?.()}
                disabled={loading}
              >
                <Paperclip
                  size={16}
                  className=" stroke-doubleNickel-gray-500"
                />
              </ActionIcon>
            </Tooltip>

            {(selectedFiles.length > 0 || attachmentsToKeep.length > 0) && (
              <div className="flex w-full flex-row gap-1 overflow-x-auto">
                {/* Existing attachments */}
                {attachmentsToKeep.map((attachment) => (
                  <Tag
                    size="xs"
                    key={attachment.emailAttachmentId}
                    onClose={() =>
                      removeExistingAttachment(attachment.emailAttachmentId)
                    }
                  >
                    {attachment.fileName} (existing)
                  </Tag>
                ))}

                {/* New files */}
                {selectedFiles.map((file, index) => (
                  <Tag
                    key={index}
                    clickable
                    onClick={() => window.open(URL.createObjectURL(file))}
                    onClose={() =>
                      setSelectedFiles(
                        selectedFiles.filter((_, i) => i !== index)
                      )
                    }
                  >
                    {file.name} (new)
                  </Tag>
                ))}
              </div>
            )}

            <Tooltip
              label="Delete template draft"
              position="bottom"
              withArrow
              arrowPosition="center"
            >
              <ActionIcon
                className="ml-auto"
                color="gray"
                variant="subtle"
                onClick={handleClose}
                disabled={loading}
              >
                <Trash2 size={16} className=" stroke-doubleNickel-gray-500" />
              </ActionIcon>
            </Tooltip>

            <DNButton
              variant="primary"
              className="min-w-24"
              loading={loading}
              disabled={!form.isValid() || loading}
              type="submit"
            >
              {isUploadingFiles && selectedFiles.length > 0
                ? `Uploading ${selectedFiles.length} file${
                    selectedFiles.length !== 1 ? 's' : ''
                  }...`
                : isSubmitting
                ? edit
                  ? 'Updating...'
                  : 'Creating...'
                : edit
                ? 'Save'
                : 'Create'}
            </DNButton>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default CreateTemplateModal
